import React, { useState } from 'react';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import { Car, DollarSign, Percent, Calendar, CreditCard, BarChart, Scale, TrendingDown } from 'lucide-react';
import { BarChart as ReBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const LeaseVsBuyCalculator = () => {
  // Vehicle details
  const [vehiclePrice, setVehiclePrice] = useState('');
  const [vehicleResaleValue, setVehicleResaleValue] = useState('');
  
  // Buy option details
  const [downPayment, setDownPayment] = useState('');
  const [loanTerm, setLoanTerm] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [annualInsurance, setAnnualInsurance] = useState('');
  const [annualMaintenance, setAnnualMaintenance] = useState('');
  
  // Lease option details
  const [leaseTerm, setLeaseTerm] = useState('');
  const [monthlyLeasePayment, setMonthlyLeasePayment] = useState('');
  const [leaseDownPayment, setLeaseDownPayment] = useState('');
  const [securityDeposit, setSecurityDeposit] = useState('');
  const [leaseAnnualInsurance, setLeaseAnnualInsurance] = useState('');
  const [annualKilometers, setAnnualKilometers] = useState('');
  const [excessKilometerCharge, setExcessKilometerCharge] = useState('');
  const [expectedExcessKilometers, setExpectedExcessKilometers] = useState('');
  
  // Calculation results
  const [result, setResult] = useState<{
    buyMonthlyPayment: number;
    buyTotalCost: number;
    buyTotalInterest: number;
    leaseMonthlyPayment: number;
    leaseTotalCost: number;
    excessKilometerCost: number;
    monthlyCostDifference: number;
    totalCostDifference: number;
    cheaperOption: 'buy' | 'lease';
    yearlyCostsBreakdown: Array<{
      year: number;
      buyCost: number;
      leaseCost: number;
      buyRunningTotal: number;
      leaseRunningTotal: number;
      difference: number;
    }>;
  } | null>(null);

  // Function to calculate and compare costs
  const calculateComparison = () => {
    // Parse inputs
    const carPrice = parseFloat(vehiclePrice);
    const resaleValue = parseFloat(vehicleResaleValue) || (carPrice * 0.4); // Default 40% resale after 5 years
    const buyDownPmt = parseFloat(downPayment) || 0;
    const buyLoanTerm = parseFloat(loanTerm) || 5; // 5 year default
    const buyRate = parseFloat(interestRate) / 100 || 0.06; // 6% default
    const buyInsurance = parseFloat(annualInsurance) || (carPrice * 0.04); // 4% default
    const maintenance = parseFloat(annualMaintenance) || (carPrice * 0.02); // 2% default

    const leaseYears = parseFloat(leaseTerm) || 3; // 3 year default
    const leasePayment = parseFloat(monthlyLeasePayment);
    const leaseDownPmt = parseFloat(leaseDownPayment) || 0;
    const deposit = parseFloat(securityDeposit) || 0;
    const leaseInsurance = parseFloat(leaseAnnualInsurance) || buyInsurance;
    const yearlyKm = parseFloat(annualKilometers) || 15000; // 15,000 km default
    const excessRate = parseFloat(excessKilometerCharge) || 0.15; // $0.15/km default
    const excessKm = parseFloat(expectedExcessKilometers) || 0;

    // Validate required inputs
    if (!carPrice || !leasePayment) {
      alert('Please enter at least the vehicle price and monthly lease payment');
      return;
    }

    // Calculate loan payment (buy option)
    const monthlyRate = buyRate / 12;
    const loanAmount = carPrice - buyDownPmt;
    const numPayments = buyLoanTerm * 12;
    
    const buyMonthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                              (Math.pow(1 + monthlyRate, numPayments) - 1);
    
    const buyTotalInterest = (buyMonthlyPayment * numPayments) - loanAmount;

    // Calculate total costs for each option
    const leaseTotalMonths = leaseYears * 12;
    const excessKilometerCost = excessKm * excessRate;
    
    // Total buy cost = Down payment + monthly payments + insurance + maintenance - resale value
    const buyTotalCost = buyDownPmt + 
                         (buyMonthlyPayment * numPayments) + 
                         (buyInsurance * buyLoanTerm) + 
                         (maintenance * buyLoanTerm) - 
                         resaleValue;
    
    // Total lease cost = Down payment + security deposit + monthly payments + insurance + excess km
    const leaseTotalCost = leaseDownPmt + 
                           (leasePayment * leaseTotalMonths) + 
                           (leaseInsurance * leaseYears) + 
                           excessKilometerCost;
    
    // Yearly breakdown for comparison
    const maxYears = Math.max(buyLoanTerm, leaseYears);
    const yearlyCostsBreakdown = [];
    
    let buyRunningTotal = buyDownPmt;
    let leaseRunningTotal = leaseDownPmt + deposit;
    
    for (let year = 1; year <= maxYears; year++) {
      // Buy costs for the year
      const yearBuyPayments = year <= buyLoanTerm ? buyMonthlyPayment * 12 : 0;
      const yearBuyInsurance = buyInsurance;
      const yearBuyMaintenance = maintenance;
      
      // Lease costs for the year
      const yearLeasePayments = year <= leaseYears ? leasePayment * 12 : 0;
      const yearLeaseInsurance = year <= leaseYears ? leaseInsurance : 0;
      const yearLeaseExcessKm = year === leaseYears ? excessKilometerCost : 0;
      
      // If last year of buy term, add resale value as a credit
      const resaleCredit = year === buyLoanTerm ? resaleValue : 0;
      
      // If last year of lease, return the security deposit
      const depositReturn = year === leaseYears ? deposit : 0;
      
      // Calculate year totals
      const yearBuyCost = yearBuyPayments + yearBuyInsurance + yearBuyMaintenance - (year === buyLoanTerm ? resaleCredit : 0);
      const yearLeaseCost = yearLeasePayments + yearLeaseInsurance + yearLeaseExcessKm - (year === leaseYears ? depositReturn : 0);
      
      buyRunningTotal += yearBuyCost;
      leaseRunningTotal += yearLeaseCost;
      
      yearlyCostsBreakdown.push({
        year,
        buyCost: yearBuyCost,
        leaseCost: yearLeaseCost,
        buyRunningTotal,
        leaseRunningTotal,
        difference: buyRunningTotal - leaseRunningTotal
      });
    }
    
    // Calculate differences
    const monthlyCostDifference = buyMonthlyPayment - leasePayment;
    const totalCostDifference = buyTotalCost - leaseTotalCost;
    const cheaperOption = totalCostDifference > 0 ? 'lease' : 'buy';
    
    // Set results
    setResult({
      buyMonthlyPayment,
      buyTotalCost,
      buyTotalInterest,
      leaseMonthlyPayment: leasePayment,
      leaseTotalCost,
      excessKilometerCost,
      monthlyCostDifference,
      totalCostDifference: Math.abs(totalCostDifference),
      cheaperOption,
      yearlyCostsBreakdown
    });
  };

  // Function to format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
  };

  // Function to get chart data
  const getChartData = () => {
    if (!result) return [];
    
    return [
      {
        name: 'Buy',
        'Monthly Payment': result.buyMonthlyPayment,
        'Total Cost': result.buyTotalCost / (parseFloat(loanTerm) || 5) / 12, // Monthly equivalent
      },
      {
        name: 'Lease',
        'Monthly Payment': result.leaseMonthlyPayment,
        'Total Cost': result.leaseTotalCost / (parseFloat(leaseTerm) || 3) / 12, // Monthly equivalent
      }
    ];
  };

  // Function to get line chart data for cumulative cost comparison
  const getCumulativeChartData = () => {
    if (!result || !result.yearlyCostsBreakdown) return [];
    return result.yearlyCostsBreakdown;
  };

  // Return component JSX
  return (
    <>
      <SEOHead
        title="Lease vs Buy Calculator Australia | Compare Car Buying & Leasing Costs"
        description="Trying to decide between leasing or buying a car in Australia? Our interactive calculator helps you compare the total costs and see which option saves you more money."
        keywords="lease vs buy calculator, car lease or buy calculator, auto leasing calculator australia, car finance comparison tool, vehicle lease vs finance, novated lease calculator, car leasing vs buying"
      />
      <Helmet>
        <title>Lease vs Buy Calculator Australia | Compare Car Buying & Leasing Costs</title>
        <meta name="description" content="Trying to decide between leasing or buying a car in Australia? Our interactive calculator helps you compare the total costs and see which option saves you more money." />
        <meta name="keywords" content="lease vs buy calculator, car lease or buy calculator, auto leasing calculator australia, car finance comparison tool, vehicle lease vs finance, novated lease calculator, car leasing vs buying" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Car Lease vs Buy Calculator",
              "description": "Interactive financial calculator that compares the costs of leasing versus buying a vehicle in Australia.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": {
                "@type": "Country",
                "name": "Australia"
              },
              "interactivityType": "active",
              "financialProductCategory": "LoanOrCredit"
            }
          `}
        </script>
      </Helmet>
      
      <div className="space-y-10">
        {/* Introduction Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Car Lease vs Buy Calculator Australia</h1>
          <div className="text-gray-600">
            <p className="mb-4">
              Deciding whether to lease or buy a car is a significant financial decision. Our calculator helps you compare the total costs 
              of both options over time, accounting for down payments, monthly payments, maintenance, insurance, and resale value.
            </p>
            <div className="bg-blue-50 p-4 rounded-md mb-4">
              <h2 className="text-lg font-semibold text-blue-800 mb-2">How This Calculator Helps You Decide:</h2>
              <ul className="list-disc pl-5 space-y-1 text-blue-700">
                <li>Compares monthly payments for leasing vs buying</li>
                <li>Calculates total cost over the full term of each option</li>
                <li>Shows year-by-year cost breakdown with running totals</li>
                <li>Includes factors like maintenance, insurance, and excess kilometers</li>
                <li>Visualizes which option costs less in your specific situation</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Calculator Sections */}
        <Calculator
          title="Lease vs Buy Comparison Calculator"
          description="Compare the costs of leasing versus buying a vehicle to find the most financially beneficial option for you."
          icon={Scale}
          result={result ? `Better Option: ${result.cheaperOption === 'buy' ? 'Buying' : 'Leasing'}` : undefined}
        >
          {/* Vehicle Details Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <Car className="text-blue-500 mr-2" size={20} />
              <h2 className="text-xl font-semibold text-gray-800">Vehicle Details</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Vehicle Price ($)"
                value={vehiclePrice}
                onChange={setVehiclePrice}
                placeholder="e.g. 30000"
                unit="$"
              />
              <Input
                label="Estimated Resale Value After Term ($)"
                value={vehicleResaleValue}
                onChange={setVehicleResaleValue}
                placeholder="Leave blank for 40% of vehicle price"
                unit="$"
              />
            </div>
          </div>

          {/* Buy Option Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <CreditCard className="text-green-500 mr-2" size={20} />
              <h2 className="text-xl font-semibold text-gray-800">Buy Option Details</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Down Payment ($)"
                value={downPayment}
                onChange={setDownPayment}
                placeholder="e.g. 5000"
                unit="$"
              />
              <Input
                label="Loan Term (Years)"
                value={loanTerm}
                onChange={setLoanTerm}
                placeholder="e.g. 5"
                unit="years"
              />
              <Input
                label="Interest Rate (%)"
                value={interestRate}
                onChange={setInterestRate}
                placeholder="e.g. 6.0"
                unit="%"
              />
              <Input
                label="Annual Insurance ($)"
                value={annualInsurance}
                onChange={setAnnualInsurance}
                placeholder="Leave blank for 4% of vehicle price"
                unit="$"
              />
              <Input
                label="Annual Maintenance ($)"
                value={annualMaintenance}
                onChange={setAnnualMaintenance}
                placeholder="Leave blank for 2% of vehicle price"
                unit="$"
              />
            </div>
          </div>

          {/* Lease Option Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <Scale className="text-purple-500 mr-2" size={20} />
              <h2 className="text-xl font-semibold text-gray-800">Lease Option Details</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Monthly Lease Payment ($)"
                value={monthlyLeasePayment}
                onChange={setMonthlyLeasePayment}
                placeholder="e.g. 500"
                unit="$"
              />
              <Input
                label="Lease Term (Years)"
                value={leaseTerm}
                onChange={setLeaseTerm}
                placeholder="e.g. 3"
                unit="years"
              />
              <Input
                label="Lease Down Payment ($)"
                value={leaseDownPayment}
                onChange={setLeaseDownPayment}
                placeholder="e.g. 2000"
                unit="$"
              />
              <Input
                label="Security Deposit ($)"
                value={securityDeposit}
                onChange={setSecurityDeposit}
                placeholder="e.g. 500"
                unit="$"
              />
              <Input
                label="Annual Insurance ($)"
                value={leaseAnnualInsurance}
                onChange={setLeaseAnnualInsurance}
                placeholder="Leave blank to use buy insurance"
                unit="$"
              />
              <Input
                label="Annual Kilometers Allowed"
                value={annualKilometers}
                onChange={setAnnualKilometers}
                placeholder="e.g. 15000"
              />
              <Input
                label="Excess Kilometer Charge ($)"
                value={excessKilometerCharge}
                onChange={setExcessKilometerCharge}
                placeholder="e.g. 0.15"
                unit="$"
              />
              <Input
                label="Expected Excess Kilometers"
                value={expectedExcessKilometers}
                onChange={setExpectedExcessKilometers}
                placeholder="e.g. 5000"
              />
            </div>
          </div>

          {/* Calculate Button */}
          <div className="flex justify-center mt-6">
            <button
              onClick={calculateComparison}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg flex items-center"
            >
              <BarChart className="mr-2" size={20} />
              Compare Lease vs Buy Costs
            </button>
          </div>

          {/* Results Section */}
          {result && (
            <div className="mt-10 space-y-8">
              {/* Summary Result */}
              <div className={`p-6 rounded-lg ${result.cheaperOption === 'buy' ? 'bg-green-50 border border-green-200' : 'bg-purple-50 border border-purple-200'}`}>
                <h3 className="text-xl font-semibold mb-4">
                  {result.cheaperOption === 'buy' ? 'Buying is Better' : 'Leasing is Better'}
                </h3>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="text-lg">
                    <p className="mb-2">
                      <span className="font-medium">
                        {result.cheaperOption === 'buy' ? 'Buying' : 'Leasing'}
                      </span> saves you approximately:
                    </p>
                    <p className="text-2xl font-bold mb-2">
                      {formatCurrency(result.totalCostDifference)}
                    </p>
                    <p className="text-md">
                      (or {formatCurrency(result.monthlyCostDifference)} per month)
                    </p>
                  </div>
                  
                  <div className="mt-4 md:mt-0 bg-white p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                      <div>
                        <p className="text-sm text-gray-600">Buy Total Cost:</p>
                        <p className="font-semibold">{formatCurrency(result.buyTotalCost)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Lease Total Cost:</p>
                        <p className="font-semibold">{formatCurrency(result.leaseTotalCost)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Buy Monthly:</p>
                        <p className="font-semibold">{formatCurrency(result.buyMonthlyPayment)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Lease Monthly:</p>
                        <p className="font-semibold">{formatCurrency(result.leaseMonthlyPayment)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Monthly Payment Comparison Chart */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Cost Comparison</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ReBarChart
                      data={getChartData()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value) => `$${value.toLocaleString()}`} />
                      <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                      <Legend />
                      <Bar dataKey="Monthly Payment" name="Monthly Payment" fill="#0088FE" />
                      <Bar dataKey="Total Cost" name="Monthly Equivalent Total Cost" fill="#00C49F" />
                    </ReBarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Year-by-Year Comparison Chart */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Cumulative Cost Over Time</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={getCumulativeChartData()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottomRight', offset: -5 }} />
                      <YAxis tickFormatter={(value) => `$${value.toLocaleString()}`} />
                      <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="buyRunningTotal" 
                        name="Buy Cumulative Cost" 
                        stroke="#0088FE" 
                        activeDot={{ r: 8 }} 
                      />
                      <Line 
                        type="monotone" 
                        dataKey="leaseRunningTotal" 
                        name="Lease Cumulative Cost" 
                        stroke="#8884d8" 
                        activeDot={{ r: 8 }} 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Yearly Breakdown Table */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Year-by-Year Cost Breakdown</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Buy Cost</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lease Cost</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Buy Running Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lease Running Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difference</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {result.yearlyCostsBreakdown.map((year, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{year.year}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(year.buyCost)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(year.leaseCost)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(year.buyRunningTotal)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(year.leaseRunningTotal)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                            <span className={year.difference > 0 ? 'text-purple-600' : 'text-green-600'}>
                              {formatCurrency(Math.abs(year.difference))} {year.difference > 0 ? 'favors lease' : 'favors buy'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </Calculator>

        {/* Information Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Lease vs Buy: Making the Right Decision</h2>
          <div className="space-y-4 text-gray-600">
            <p>
              When deciding between leasing or buying a vehicle in Australia, there are many factors to consider beyond just the monthly payment.
              Understanding the total cost of each option can help you make a more informed decision.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-700 mt-4">Advantages of Buying</h3>
            <ul className="list-disc ml-6 space-y-1">
              <li>Build equity in the vehicle over time</li>
              <li>No kilometer restrictions or excess charges</li>
              <li>Freedom to modify the vehicle</li>
              <li>Lower long-term costs if you keep the vehicle beyond the loan term</li>
              <li>No condition penalties when returning the vehicle</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-gray-700 mt-4">Advantages of Leasing</h3>
            <ul className="list-disc ml-6 space-y-1">
              <li>Lower monthly payments (typically)</li>
              <li>Lower up-front costs</li>
              <li>Ability to drive a new vehicle every few years</li>
              <li>Warranty coverage for most or all of the lease term</li>
              <li>Potential tax benefits for business use</li>
              <li>Avoid hassle of selling the vehicle later</li>
            </ul>
            
            <div className="bg-blue-50 p-5 rounded-lg mt-6">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Consider These Factors</h3>
              <ul className="list-disc ml-6 space-y-2 text-blue-700">
                <li><strong>How long you plan to keep the vehicle:</strong> Leasing typically makes more sense if you like to change vehicles every few years</li>
                <li><strong>Your annual kilometers:</strong> High-mileage drivers often benefit more from buying</li>
                <li><strong>Tax implications:</strong> For business use, lease payments may be tax-deductible</li>
                <li><strong>Vehicle depreciation:</strong> Some vehicles hold their value better than others, affecting the buy equation</li>
                <li><strong>Your maintenance habits:</strong> If you tend to neglect maintenance, leasing may result in costly end-of-lease penalties</li>
              </ul>
            </div>
            
            <p className="mt-6">
              Our calculator helps you quantify the financial differences between leasing and buying, but remember to consider these
              non-financial factors as well when making your decision.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeaseVsBuyCalculator; 